<template>
  <picture :class="[bemClass(), imgFit]" ref="el">
    <transition-group name="fade" mode="in-out">
      <i :class="bemClass('loader')" v-if="loader"></i>
      <source
        v-for="(srcset, index) in sourcesArr"
        :media="breakpointsArr[index]"
        :data-srcset="srcset"
        :sizes="sizes[index]"
        :key="sourcesArr[index]"
        :ref="setSourceEls">
      <noscript key="noscriptImg">
        <img :class="[bemClass('img'), 'is-fallback']" :src="sourcesArr[sourcesArr.length - 1]" :alt="alt">
      </noscript>
      <img
        key="img"
        :class="bemClass('img')"
        :data-src="sourcesArr[sourcesArr.length - 1]"
        :alt="alt"
        ref="img"
        loading="lazy">
    </transition-group>
  </picture>
</template>

<script>
import { onMounted, ref, watch, nextTick, computed, toRefs } from 'vue'
import useBem from '../../compositions/bem'
import { useRefs } from '../../compositions/template-ref'
import { init } from '../../util/picture'

export default {
  name: 'SelImage',
  props: {
    sources: {
      type: [String, Array],
      required: true
    },
    breakpoints: {
      type: [String, Array],
      default: () => []
    },
    sizes: {
      type: [String, Array],
      default: () => []
    },
    alt: String,
    loader: {
      type: Boolean,
      default: false
    },
    fit: {
      type: String,
      default: 'cover'
    },
    modifier: {
      type: String,
      default: ''
    }
  },
  setup (props) {
    const { modifier } = toRefs(props)
    const [sourceEls, setSourceEls] = useRefs()
    const { bemClass } = useBem('sel-picture', modifier)
    const el = ref(null)
    const img = ref(null)
    const imgFit = `fit-${props.fit}`

    const sourcesArr = computed(() => {
      return Array.isArray(props.sources) ? props.sources : props.sources.split('|')
    })

    const breakpointsArr = computed(() => {
      return Array.isArray(props.breakpoints) ? props.breakpoints : props.breakpoints.split('|')
    })

    watch(sourcesArr, () => {
      nextTick(() => {
        init(el.value, img.value, sourceEls.value, true)
      })
    }, { deep: true })

    onMounted(() => {
      nextTick(() => {
        init(el.value, img.value, sourceEls.value, true)
      })
    })

    return {
      bemClass,
      el,
      setSourceEls,
      sourceEls,
      img,
      imgFit,
      sourcesArr,
      breakpointsArr
    }
  }
}
</script>

<style scoped>
.sel-picture,
.sel-picture__img {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: block;
  height: 100%;
  width: 100%;
}

.sel-picture {
  overflow: hidden;

  &.fit-cover {
    .sel-picture__img {
      object-fit: cover;
      /* stylelint-disable */
      font-family: 'object-fit: cover;'; /* For polyfill */
      /* stylelint-enable */
    }
  }

  &.fit-contain {
    .sel-picture__img {
      object-fit: contain;
      /* stylelint-disable */
      font-family: 'object-fit: contain;'; /* For polyfill */
      /* stylelint-enable */
    }
  }

  &.fit-width {
    position: relative;

    .sel-picture__img {
      position: relative;
      bottom: auto;
      width: 100%;
      height: 100%;
    }
  }

  &.is-loaded {
    .sel-picture__img {
      opacity: 1;
      visibility: visible;
    }

    .sel-picture__loader {
      opacity: 0;
      visibility: hidden;
    }
  }
}

.sel-picture__img {
  opacity: 0;
  transition-duration: 0.25s;
  transition-property: opacity, visibility;
  transition-timing-function: ease-in-out;
  visibility: hidden;

  &.is-fallback {
    opacity: 1;
    visibility: visible;

    /* Hide default img element if fallback is active */
    & + .sel-picture__img {
      display: none;
    }
  }
}

.sel-picture__loader {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 50%;
  height: 50%;
  max-width: 30px;
  max-height: 30px;
  transform: translate3d(-50%, -50%, 0);
  background-color: LightGrey;

  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    background-color: Grey;
    animation: spin 2s infinite;
    animation-timing-function: linear;
  }
}
</style>
